/* eslint-disable import/no-extraneous-dependencies */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import { SvgIcon } from '@mui/material';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@digital-hig/typography';
import Skeleton from '@mui/material/Skeleton';
import SvgExternal from '@digital-hig/icon/lib/build/icons/navigation/external';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'isomorphic-dompurify';
import OverviewRootElement from './style';
import { ProfileContextConsumer } from '../../../common/context/profile/context';
import { OVERVIEW_PARAGRAPH_LENGTH } from '../../../constants/app-constants';

const Overview = ({ partnerDetails, loading, scrollRef }) => {
  const {
    body_overview: bodyOverview, logo_url: logoUrl, display_name: displayName, other_websites: otherWebsites,
  } = partnerDetails || {};
  const [imgLoading, setImgLoading] = useState(true);
  const { t } = useTranslation();
  const { t: translate } = useTranslation('detail');
  const sanitizedHtml = DOMPurify.sanitize(bodyOverview, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'img', 'p', 'div', 'span', 'br', 'ul', 'li', 'ol'],
    ALLOWED_ATTR: ['href', 'target', 'src', 'alt', 'style'],
  });

  return (
    // eslint-disable-next-line
    <OverviewRootElement ref={(el) => (scrollRef.current[0] = el)}>
      <Box className="page-container">
        <Box className="overview-section">
          <Box className="left-section">
            {
              loading ? <Skeleton height={50} width="50%" />
                : (
                  <Typography variant="headline-medium" mb={6} component="h3">
                    {`${displayName} ${t('Overview')}`}
                  </Typography>
                )
            }
            <Box className="overview-logo">
              {logoUrl && (
                <CardMedia
                  alt={translate(displayName)}
                  component="img"
                  height="172"
                  image={logoUrl}
                  onLoad={() => setImgLoading(false)}
                  data-testid="overview-media"
                />
              )}
              {imgLoading && (
                <Skeleton
                  className="shimmer-card"
                  variant="rectangular"
                  width="100%"
                  height="100%"
                />
              )}
            </Box>
            <Box className="overview-links">
              {!loading && otherWebsites?.slice(0, 3)?.map((website, index) => (
                <Link
                  // eslint-disable-next-line react/no-array-index-key
                  key={`website-${index}`}
                  href={website?.URL}
                  variant="headline-smaller"
                  underline="none"
                  target="_blank"
                >
                  <SvgIcon fontSize="medium"><SvgExternal /></SvgIcon>
                  {website?.Display}
                </Link>
              ))}
              {loading && [...Array(3).keys()].map((idx) => (
                <Skeleton key={idx} width="50%" />
              ))}
            </Box>
          </Box>
          <Box className="right-section">
            {!loading && <Typography dangerouslySetInnerHTML={{ __html: translate(sanitizedHtml) }} />}
            {loading && [...Array(OVERVIEW_PARAGRAPH_LENGTH).keys()].map((idx) => (
              <Skeleton key={idx} />
            ))}
          </Box>
        </Box>
      </Box>
    </OverviewRootElement>
  );
};

Overview.propTypes = {
  partnerDetails: PropTypes.shape({}),
  loading: PropTypes.bool,
  scrollRef: PropTypes.shape({}),
};
Overview.defaultProps = {
  partnerDetails: {},
  loading: false,
  scrollRef: {},
};
export default ProfileContextConsumer(Overview);
